import type { AxiosRequestConfig } from 'axios';
import api from './api';

/**
 * The raffle's HTTP door (#92).
 *
 * Two things separate this from calling `api` directly, and both matter only
 * because the control panel is driven live, in front of a room.
 *
 * **1. The panel must show the admin's language, not the event's.** The backend
 * returns a machine-readable `code` next to a human `message`, and that message
 * goes out in `Event.language` — the seeded conference is Hungarian, so an
 * English-speaking organizer would read a Hungarian sentence off a rejected
 * button press. `raffle-error-codes.ts` and the `PUBLIC_ERROR_CODES` list in
 * `all-exceptions.filter.ts` both spell the rule out: branch on `code`, never
 * on `message`. `raffleErrorKey` below is the client half of that contract.
 *
 * **2. The shared response interceptor would toast the wrong sentence anyway.**
 * `config/api.ts` calls `message.error(error.response.data.message)` for every
 * rejected request. Mapping the code in the component would therefore not
 * replace that toast, it would ADD a second one — the admin's language and the
 * event's language stacked on top of each other, mid-draw. Since the
 * interceptor only reacts to a REJECTED promise, the helpers here resolve the
 * business failures instead and raise their own error afterwards, which leaves
 * the panel as the single voice on screen.
 *
 * `401` is deliberately left rejecting: that is the status the interceptor
 * refreshes the token on, and a session expiring between two prizes has to
 * recover on its own rather than drop the organizer on the login page.
 */

/** Resolve everything but `401`, so the interceptor stays out of the way. */
const RAFFLE_VALIDATE_STATUS = (status: number): boolean => status !== 401;

/** A failed raffle call, carrying the backend's machine-readable code. */
export class RaffleRequestError extends Error {
  readonly status: number;

  readonly code?: string;

  constructor(status: number, code?: string) {
    super(`Raffle request failed with status ${status}`);
    this.name = 'RaffleRequestError';
    this.status = status;
    this.code = code;
  }
}

/** The error body shape of `all-exceptions.filter.ts`. */
interface RaffleErrorBody {
  code?: string;
}

/**
 * Every code the filter is allowed to emit, mapped onto our own copy.
 *
 * The keys mirror `RAFFLE_ERROR_CODES` in the backend. A code missing from this
 * table falls back to the generic string rather than rendering the raw
 * identifier — an organizer should never read `RAFFLE_NO_ELIGIBLE_ENTRIES` off
 * a screen.
 */
const RAFFLE_ERROR_KEYS = {
  RAFFLE_DRAW_NOT_FOUND: 'raffle.errors.drawNotFound',
  RAFFLE_DRAW_CLOSED: 'raffle.errors.drawClosed',
  RAFFLE_INVALID_TRANSITION: 'raffle.errors.invalidTransition',
  RAFFLE_IDENTITY_REQUIRED: 'raffle.errors.identityRequired',
  RAFFLE_NO_ELIGIBLE_ENTRIES: 'raffle.errors.noEligibleEntries',
  RAFFLE_NAME_COLLISION: 'raffle.errors.nameCollision',
  RAFFLE_NO_ACTIVE_WINNER: 'raffle.errors.noActiveWinner',
  RAFFLE_PRIZE_ALREADY_AWARDED: 'raffle.errors.prizeAlreadyAwarded',
  RAFFLE_DRAW_IN_PROGRESS: 'raffle.errors.drawInProgress',
} as const;

/**
 * The i18n keys this module can produce.
 *
 * Deliberately a literal union rather than `string`. `types/react-i18next.d.ts`
 * types `t()` against `hu.json`, so a mistyped key is a compile error instead
 * of a button captioned `raffle.errors.drawClosd` — widening the return type
 * here would quietly opt every raffle screen out of that guarantee.
 */
export type RaffleErrorKey =
  | (typeof RAFFLE_ERROR_KEYS)[keyof typeof RAFFLE_ERROR_KEYS]
  | 'raffle.errors.generic';

/**
 * `Object.hasOwn`, not `in`: the `in` operator walks the prototype chain, so a
 * server `code` of `toString`, `constructor`, `valueOf` or `hasOwnProperty`
 * would pass this guard and then hand `t()` an inherited *function* instead of
 * a key — the organizer would see a stack of JavaScript source where an error
 * message belongs, mid-draw. `src/i18n/registrationStatus.ts` guards the same
 * shape the same way for the same reason.
 */
function isKnownCode(code: string): code is keyof typeof RAFFLE_ERROR_KEYS {
  return Object.hasOwn(RAFFLE_ERROR_KEYS, code);
}

/**
 * The i18n key describing why a raffle call failed.
 *
 * Returns a key rather than a finished sentence so the caller translates it
 * inside React, where a language switch actually re-renders.
 */
export function raffleErrorKey(error: unknown): RaffleErrorKey {
  if (error instanceof RaffleRequestError && error.code && isKnownCode(error.code)) {
    return RAFFLE_ERROR_KEYS[error.code];
  }
  return 'raffle.errors.generic';
}

async function raffleRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await api.request<T | RaffleErrorBody>({
    ...config,
    validateStatus: RAFFLE_VALIDATE_STATUS,
    // The raffle screens are the single voice for their own errors: business
    // 4xx already resolve here and are translated from `code`, and a transport
    // failure is shown as one quiet "no connection" marker rather than a toast
    // per request. Without this the shared interceptor still fired on network
    // errors — which is exactly the case a background poll produces over and
    // over while the backend is down.
    silentError: true,
  } as AxiosRequestConfig);

  if (response.status >= 400) {
    const body = (response.data ?? {}) as RaffleErrorBody;
    throw new RaffleRequestError(response.status, body.code);
  }

  return response.data as T;
}

export function raffleGet<T>(url: string, params?: object): Promise<T> {
  return raffleRequest<T>({ method: 'get', url, params });
}

export function rafflePost<T>(url: string, data?: object): Promise<T> {
  return raffleRequest<T>({ method: 'post', url, data });
}

export function rafflePatch<T>(url: string, data?: object): Promise<T> {
  return raffleRequest<T>({ method: 'patch', url, data });
}

export function raffleDelete<T>(url: string): Promise<T> {
  return raffleRequest<T>({ method: 'delete', url });
}
